/**
 * HTML sanitizing/normalizing helpers for the RichTextEditor.
 *
 * Pasted content (Excel, Outlook, Word, Google Docs/Sheets, web pages) is
 * converted to clean HTML that keeps the visible formatting - tables, lists,
 * bold/italic/underline, colors, fonts, alignment - while dropping anything
 * unsafe (scripts, event handlers, javascript: links...). e.g.
 * `<p>This is <strong>bold</strong> and <em>italic</em> text.</p>`
 */

// Tags removed together with their content
const DROPPED_TAGS = [
  'SCRIPT',
  'STYLE',
  'META',
  'LINK',
  'TITLE',
  'HEAD',
  'XML',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'TEMPLATE',
  'NOSCRIPT',
  'SVG',
  'FORM',
  'INPUT',
  'BUTTON',
  'SELECT',
  'TEXTAREA'
];

// Allowed tags mapped to their normalized output tag
const TAG_MAP = {
  P: 'p',
  DIV: 'p',
  H1: 'h1',
  H2: 'h2',
  H3: 'h3',
  H4: 'h4',
  H5: 'h5',
  H6: 'h6',
  BLOCKQUOTE: 'blockquote',
  PRE: 'pre',
  UL: 'ul',
  OL: 'ol',
  LI: 'li',
  BR: 'br',
  HR: 'hr',
  STRONG: 'strong',
  B: 'strong',
  EM: 'em',
  I: 'em',
  U: 'u',
  INS: 'u',
  S: 's',
  STRIKE: 's',
  DEL: 's',
  SUP: 'sup',
  SUB: 'sub',
  A: 'a',
  IMG: 'img',
  TABLE: 'table',
  CAPTION: 'caption',
  THEAD: 'thead',
  TBODY: 'tbody',
  TFOOT: 'tfoot',
  TR: 'tr',
  TD: 'td',
  TH: 'th',
  COLGROUP: 'colgroup',
  COL: 'col'
};

// Blocks whose children are inline content (nested paragraphs get flattened)
const TEXT_BLOCKS = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'pre'];
// Containers that may hold paragraphs
const BLOCK_CONTAINERS = ['blockquote', 'td', 'th', 'caption'];
const BLOCK_TAGS = TEXT_BLOCKS.concat([
  'ul',
  'ol',
  'blockquote',
  'table',
  'hr'
]);
const SOURCE_BLOCK_TAGS = Object.keys(TAG_MAP).filter(
  name => BLOCK_TAGS.indexOf(TAG_MAP[name]) !== -1 || name === 'DIV'
);

const TEXT_STYLES = ['color', 'background-color', 'font-family', 'font-size'];
const SPACING_STYLES = [
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left'
];
const BORDER_STYLES = ['top', 'right', 'bottom', 'left'].reduce(
  (list, side) =>
    list.concat([
      `border-${side}-width`,
      `border-${side}-style`,
      `border-${side}-color`
    ]),
  []
);
const BLOCK_STYLES = TEXT_STYLES.concat(SPACING_STYLES, [
  'text-align',
  'text-indent',
  'line-height'
]);
const LIST_STYLES = SPACING_STYLES.concat(['list-style-type']);
const CELL_STYLES = BLOCK_STYLES.concat(BORDER_STYLES, [
  'vertical-align',
  'width',
  'height',
  'white-space'
]);

// CSS properties kept per output tag
const ALLOWED_STYLES = {
  p: BLOCK_STYLES,
  h1: BLOCK_STYLES,
  h2: BLOCK_STYLES,
  h3: BLOCK_STYLES,
  h4: BLOCK_STYLES,
  h5: BLOCK_STYLES,
  h6: BLOCK_STYLES,
  blockquote: BLOCK_STYLES.concat(BORDER_STYLES),
  pre: BLOCK_STYLES,
  li: BLOCK_STYLES.concat(['list-style-type']),
  ul: LIST_STYLES,
  ol: LIST_STYLES,
  table: SPACING_STYLES.concat(BORDER_STYLES, [
    'width',
    'border-collapse',
    'background-color'
  ]),
  tr: ['height', 'background-color'],
  td: CELL_STYLES,
  th: CELL_STYLES,
  col: ['width'],
  img: ['width', 'height'],
  hr: []
};

// Attributes kept per output tag (href/src are validated separately)
const ALLOWED_ATTRIBUTES = {
  a: ['href', 'title'],
  img: ['src', 'alt', 'width', 'height'],
  table: ['border', 'cellpadding', 'cellspacing', 'width'],
  td: ['colspan', 'rowspan', 'width', 'align', 'valign'],
  th: ['colspan', 'rowspan', 'width', 'align', 'valign'],
  col: ['span', 'width'],
  colgroup: ['span'],
  ol: ['start', 'type']
};

const SAFE_HREF = /^(https?:|mailto:|tel:|#)/i;
const SAFE_SRC = /^https?:/i;
const UNSAFE_STYLE_VALUE = /url\s*\(|expression\s*\(|javascript:|behavior\s*:/i;
const IGNORED_STYLE_VALUES = ['initial', 'inherit', 'unset', 'revert'];
const FONT_SIZES = ['', '10px', '13px', '16px', '18px', '24px', '32px', '48px'];
const BULLET_MARKER = /^[·•▪■◦○o§\-–—*➢Ø✓]$/;

// Applied to pasted tables so they render as a grid in the email
const CELL_BORDER = '1px solid #999999';
const CELL_PADDING = '4px 6px';

const forEach = (list, fn) => Array.prototype.forEach.call(list, fn);

const isBoldWeight = weight =>
  weight === 'bold' || weight === 'bolder' || parseInt(weight, 10) >= 600;

/**
 * Word and Excel put most formatting in a <style> block (MsoNormal, xl65...).
 * Copy those class rules onto the elements as inline styles, without
 * overriding styles that were set inline.
 */
const inlineStyleSheets = doc => {
  const inlineProps = new Map();
  const getInlineProps = el => {
    if (!inlineProps.has(el)) {
      const props = {};
      for (let i = 0; i < el.style.length; i += 1) props[el.style[i]] = true;
      inlineProps.set(el, props);
    }
    return inlineProps.get(el);
  };

  forEach(doc.querySelectorAll('style'), styleEl => {
    // Parse in the live document (rules disabled through media) to get CSSOM
    const sheetEl = document.createElement('style');
    sheetEl.setAttribute('media', 'not all');
    sheetEl.textContent = styleEl.textContent;
    document.head.appendChild(sheetEl);
    try {
      forEach(sheetEl.sheet ? sheetEl.sheet.cssRules : [], rule => {
        if (rule.type !== 1 || !doc.body) return; // style rules only
        let matches = [];
        try {
          matches = doc.body.querySelectorAll(rule.selectorText);
        } catch (e) {
          return; // selector not supported
        }
        forEach(matches, el => {
          const inline = getInlineProps(el);
          for (let i = 0; i < rule.style.length; i += 1) {
            const name = rule.style[i];
            if (!inline[name]) {
              el.style.setProperty(name, rule.style.getPropertyValue(name));
            }
          }
        });
      });
    } finally {
      document.head.removeChild(sheetEl);
    }
  });
};

/**
 * Word list items are paragraphs with an `mso-list` style and a fake bullet
 * span. Mark them (before CSSOM drops the mso-* properties) so they can be
 * turned into real <ul>/<ol> lists.
 */
const markWordMarkup = doc => {
  forEach(doc.querySelectorAll('[style]'), el => {
    const style = el.getAttribute('style');
    if (/mso-list\s*:\s*ignore/i.test(style)) {
      el.setAttribute('data-rte-ignore', '');
      return;
    }
    const highlight = /mso-highlight\s*:\s*([^;]+)/i.exec(style);
    if (highlight) el.setAttribute('data-rte-highlight', highlight[1].trim());

    const level = /mso-list\s*:[^;]*level(\d+)/i.exec(style);
    if (level && /^(P|H\d|DIV)$/.test(el.tagName)) {
      let marker = '';
      forEach(el.querySelectorAll('[style]'), child => {
        if (
          !marker &&
          /mso-list\s*:\s*ignore/i.test(child.getAttribute('style'))
        ) {
          marker = child.textContent.replace(/[\s\u00a0]/g, '');
        }
      });
      el.setAttribute('data-rte-level', level[1]);
      el.setAttribute(
        'data-rte-list',
        marker && !BULLET_MARKER.test(marker) ? 'ol' : 'ul'
      );
    }
  });
};

const isSafeStyleValue = value =>
  !!value &&
  IGNORED_STYLE_VALUES.indexOf(value) === -1 &&
  !UNSAFE_STYLE_VALUE.test(value);

const copyStyles = (from, to, props) => {
  props.forEach(prop => {
    const value = from.style.getPropertyValue(prop);
    if (isSafeStyleValue(value)) to.style.setProperty(prop, value);
  });
};

/**
 * Legacy <font> and Word highlight markup expressed as CSS.
 */
const legacyStyles = (node, out) => {
  if (node.tagName === 'FONT') {
    const color = node.getAttribute('color');
    const face = node.getAttribute('face');
    const size = parseInt(node.getAttribute('size'), 10);
    if (color && isSafeStyleValue(color)) out.style.setProperty('color', color);
    if (face && isSafeStyleValue(face)) {
      out.style.setProperty('font-family', face);
    }
    if (FONT_SIZES[size]) out.style.setProperty('font-size', FONT_SIZES[size]);
  }
  const highlight = node.getAttribute('data-rte-highlight');
  if (highlight && isSafeStyleValue(highlight)) {
    out.style.setProperty('background-color', highlight);
  }
};

/**
 * Resolves the output tag for an element, taking inline styles into account
 * (Google Docs wraps everything in `<b style="font-weight:normal">`).
 */
const resolveTag = el => {
  if (el.hasAttribute('data-rte-level')) return 'li';
  let tag = TAG_MAP[el.tagName] || null;
  const fontWeight = el.style.fontWeight || '';
  if (tag === 'strong' && fontWeight && !isBoldWeight(fontWeight)) {
    return null;
  }
  // A <div> wrapping other blocks is just a container
  if (el.tagName === 'DIV') {
    const hasBlocks = Array.prototype.some.call(
      el.children,
      child => SOURCE_BLOCK_TAGS.indexOf(child.tagName) !== -1
    );
    if (hasBlocks) tag = null;
  }
  if (tag === 'a' && !SAFE_HREF.test(el.getAttribute('href') || '')) {
    return null;
  }
  if (tag === 'img' && !SAFE_SRC.test(el.getAttribute('src') || '')) {
    return 'skip';
  }
  return tag;
};

/**
 * Inline formatting expressed through CSS (common in pasted content),
 * returned as the list of semantic wrapper tags to apply.
 */
const styleWrappers = (el, tag) => {
  const style = el.style;
  const wrappers = [];
  if (
    tag !== 'strong' &&
    !/^(h[1-6]|th)$/.test(tag || '') &&
    isBoldWeight(style.fontWeight || '')
  ) {
    wrappers.push('strong');
  }
  if (tag !== 'em' && style.fontStyle === 'italic') wrappers.push('em');
  const decoration = `${style.textDecoration ||
    ''} ${style.textDecorationLine || ''}`;
  if (tag !== 'u' && decoration.indexOf('underline') !== -1) {
    wrappers.push('u');
  }
  if (tag !== 's' && decoration.indexOf('line-through') !== -1) {
    wrappers.push('s');
  }
  if (!tag) {
    if (style.verticalAlign === 'super') wrappers.push('sup');
    if (style.verticalAlign === 'sub') wrappers.push('sub');
  }
  return wrappers;
};

const cleanChildren = (node, doc, ctx) => {
  const fragment = doc.createDocumentFragment();
  forEach(node.childNodes, child => {
    const cleaned = cleanNode(child, doc, ctx); // eslint-disable-line no-use-before-define
    if (cleaned) fragment.appendChild(cleaned);
  });
  return fragment;
};

const cleanNode = (node, doc, ctx) => {
  if (node.nodeType === 3) {
    const text = node.nodeValue;
    if (ctx.pre) return doc.createTextNode(text);
    // Whitespace-only source formatting between blocks/table rows
    if (!ctx.inBlock && /^\s*$/.test(text) && /[\r\n]/.test(text)) {
      return null;
    }
    // Line breaks in the HTML source are just spaces when rendered
    return doc.createTextNode(text.replace(/[ \t]*[\r\n]+[ \t]*/g, ' '));
  }
  if (node.nodeType !== 1 || DROPPED_TAGS.indexOf(node.tagName) !== -1) {
    return null; // comments, conditional comments, scripts, styles...
  }
  if (node.hasAttribute('data-rte-ignore')) return null; // Word fake bullets

  const tag = resolveTag(node);
  if (tag === 'skip') return null;
  if (tag === 'br') return doc.createElement('br');

  const whiteSpace = node.style.whiteSpace || '';
  const childCtx = {
    pre: ctx.pre || tag === 'pre' || /^pre/.test(whiteSpace),
    inBlock:
      TEXT_BLOCKS.indexOf(tag) !== -1 ||
      (BLOCK_CONTAINERS.indexOf(tag) === -1 && ctx.inBlock)
  };

  // Paragraphs nested inside a paragraph/heading/list item are flattened
  if (TEXT_BLOCKS.indexOf(tag) !== -1 && tag !== 'li' && ctx.inBlock) {
    const fragment = cleanChildren(node, doc, ctx);
    const next = node.nextSibling;
    if (next && (next.nodeType === 1 || next.nodeValue.trim())) {
      fragment.appendChild(doc.createElement('br'));
    }
    return fragment;
  }

  let content = cleanChildren(node, doc, childCtx);

  // Formatting expressed through inline CSS becomes semantic tags
  styleWrappers(node, tag)
    .reverse()
    .forEach(wrapperTag => {
      const wrapper = doc.createElement(wrapperTag);
      wrapper.appendChild(content);
      content = wrapper;
    });

  // Unknown inline tags (span, font, o:p...) are kept only for their styles
  const outTag = tag || 'span';
  const el = doc.createElement(outTag);
  copyStyles(node, el, ALLOWED_STYLES[outTag] || TEXT_STYLES);
  legacyStyles(node, el);
  if (!tag && childCtx.pre && !ctx.pre) el.style.whiteSpace = whiteSpace;

  if (outTag === 'li' && node.hasAttribute('data-rte-level')) {
    // The list itself provides the indentation Word fakes with margins
    ['margin', 'margin-left', 'text-indent', 'padding-left'].forEach(prop =>
      el.style.removeProperty(prop)
    );
    el.setAttribute('data-rte-level', node.getAttribute('data-rte-level'));
    el.setAttribute('data-rte-list', node.getAttribute('data-rte-list'));
  }
  if (el.getAttribute('style') === '') el.removeAttribute('style');
  if (!tag && (!el.hasAttribute('style') || !content.childNodes.length)) {
    return content;
  }

  (ALLOWED_ATTRIBUTES[outTag] || []).forEach(name => {
    const value = node.getAttribute(name);
    if (value !== null && !/^\s*javascript:/i.test(value)) {
      el.setAttribute(name, value);
    }
  });
  if (outTag === 'a') {
    el.setAttribute('target', '_blank');
    el.setAttribute('rel', 'noopener noreferrer');
  }
  el.appendChild(content);
  return el;
};

/**
 * Groups the Word list paragraphs (marked `data-rte-level`) into real,
 * possibly nested, <ul>/<ol> lists.
 */
const groupWordLists = (root, doc) => {
  const parents = [];
  forEach(root.querySelectorAll('li[data-rte-level]'), li => {
    if (parents.indexOf(li.parentNode) === -1) parents.push(li.parentNode);
  });

  parents.forEach(parent => {
    let stack = [];
    Array.prototype.slice.call(parent.childNodes).forEach(child => {
      const isWordItem =
        child.nodeType === 1 && child.hasAttribute('data-rte-level');
      if (!isWordItem) {
        if (child.nodeType === 1 || child.nodeValue.trim()) stack = [];
        return;
      }
      const level = parseInt(child.getAttribute('data-rte-level'), 10) || 1;
      const type = child.getAttribute('data-rte-list');
      child.removeAttribute('data-rte-level');
      child.removeAttribute('data-rte-list');

      while (stack.length > level) stack.pop();
      if (
        stack.length === level &&
        stack[level - 1].tagName.toLowerCase() !== type
      ) {
        stack.pop();
      }
      while (stack.length < level) {
        const list = doc.createElement(type);
        if (stack.length) {
          const owner = stack[stack.length - 1];
          (owner.lastElementChild || owner).appendChild(list);
        } else {
          parent.insertBefore(list, child);
        }
        stack.push(list);
      }
      stack[stack.length - 1].appendChild(child);
    });
  });
};

const hasBorder = el =>
  ['top', 'right', 'bottom', 'left'].some(side => {
    const style = el.style.getPropertyValue(`border-${side}-style`);
    const width = el.style.getPropertyValue(`border-${side}-width`);
    return style && style !== 'none' && style !== 'hidden' && width !== '0px';
  });

/**
 * Makes tables render as a readable grid in the email: collapsed borders,
 * and a border + padding on cells that have none (Excel/Sheets copies
 * often rely on gridlines that don't exist outside the spreadsheet).
 */
const normalizeTables = root => {
  forEach(root.querySelectorAll('table'), table => {
    table.style.setProperty('border-collapse', 'collapse');
    table.removeAttribute('border');
    forEach(table.querySelectorAll('td, th'), cell => {
      if (!hasBorder(cell)) cell.style.setProperty('border', CELL_BORDER);
      if (!cell.style.getPropertyValue('padding-top')) {
        cell.style.setProperty('padding', CELL_PADDING);
      }
      if (!cell.style.getPropertyValue('vertical-align')) {
        cell.style.setProperty('vertical-align', 'top');
      }
    });
  });
};

/**
 * Wraps loose top-level text/inline nodes into `<p>` elements so the output
 * is always a list of block elements.
 */
const wrapLooseInline = (root, doc) => {
  let paragraph = null;
  Array.prototype.slice.call(root.childNodes).forEach(child => {
    const isBlock =
      child.nodeType === 1 &&
      BLOCK_TAGS.indexOf(child.tagName.toLowerCase()) !== -1;
    if (isBlock) {
      paragraph = null;
      return;
    }
    if (!paragraph) {
      if (child.nodeType === 3 && !child.nodeValue.trim()) {
        root.removeChild(child);
        return;
      }
      paragraph = doc.createElement('p');
      root.insertBefore(paragraph, child);
    }
    paragraph.appendChild(child);
  });

  // A trailing <br> in a block is only there to keep it from collapsing
  forEach(root.querySelectorAll(TEXT_BLOCKS.join(',')), block => {
    const last = block.lastChild;
    if (last && last.nodeName === 'BR' && last.previousSibling) {
      block.removeChild(last);
    }
  });
};

/**
 * Sanitizes and normalizes an HTML string.
 * Returns an empty string when the content has nothing visible.
 *
 * @param {string} html
 * @returns {string}
 */
export const sanitizeHtml = html => {
  if (!html) return '';
  const doc = new window.DOMParser().parseFromString(html, 'text/html');
  if (!doc.body) return '';

  markWordMarkup(doc);
  if (doc.querySelector('style')) inlineStyleSheets(doc);

  const root = doc.createElement('div');
  root.appendChild(
    cleanChildren(doc.body, doc, { inBlock: false, pre: false })
  );
  groupWordLists(root, doc);
  normalizeTables(root);
  wrapLooseInline(root, doc);

  const isEmpty =
    root.textContent.replace(/\u00a0/g, ' ').trim() === '' &&
    !root.querySelector('img, hr, table');
  return isEmpty ? '' : root.innerHTML;
};

const escapeText = value =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/ {2}/g, ' &nbsp;');

/**
 * Converts tab-separated text (spreadsheet copies that only provide plain
 * text) into a table.
 */
const tsvToTable = lines => {
  const rows = lines.map(line => {
    const cells = line
      .split('\t')
      .map(cell => `<td>${escapeText(cell) || '<br>'}</td>`)
      .join('');
    return `<tr>${cells}</tr>`;
  });
  return sanitizeHtml(`<table><tbody>${rows.join('')}</tbody></table>`);
};

/**
 * Converts plain text into paragraph HTML (used when pasting plain text).
 * Tab-separated rows become a table.
 *
 * @param {string} text
 * @returns {string}
 */
export const textToHtml = text => {
  const lines = text
    .replace(/\r\n?/g, '\n')
    .replace(/\n$/, '')
    .split('\n');
  if (lines.length > 1 && lines.every(line => line.indexOf('\t') !== -1)) {
    return tsvToTable(lines);
  }
  return lines
    .map(
      line =>
        `<p>${escapeText(line).replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;') ||
          '<br>'}</p>`
    )
    .join('');
};
